import type { Id, IsoDateTime } from "../primitives";

/**
 * Where a document sits.
 *
 * `path` is a materialised path — `/handbook/warehouse/receiving` — stored
 * rather than walked. A tree query that climbs parent by parent costs one
 * round trip per level, and the permission check in FR-DOC-8 has to climb to
 * the nearest ancestor carrying a grant on every read.
 */
export interface DocFolder {
  id: Id;
  parentId: Id | null;
  name: string;
  slug: string;
  /** Materialised, leading slash, no trailing slash. Root is `""`. */
  path: string;
  createdByUserId: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/**
 * A document.
 *
 * The body lives on revisions, not here: every save is a new immutable
 * revision (FR-DOC-3), and a page holding its own markdown would be a second
 * copy that could disagree with the revision it claims to be at.
 */
export interface DocPage {
  id: Id;
  folderId: Id;
  title: string;
  slug: string;
  currentRevisionId: Id;
  /** Soft delete (FR-DOC-13), recoverable by anyone holding Manage. */
  deletedAt: IsoDateTime | null;
  /** Who deleted it. Null exactly when `deletedAt` is null. */
  deletedByUserId: Id | null;
  createdByUserId: Id;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

/**
 * One saved version of a page. Immutable, always.
 *
 * Restoring an old revision writes a *new* revision carrying its content
 * (FR-DOC-6). Rewriting history would make the audit trail a claim rather
 * than a record.
 */
export interface DocRevision {
  id: Id;
  pageId: Id;
  markdown: string;
  authorUserId: Id;
  /** What changed and why. Optional — most saves are not worth a sentence. */
  message: string | null;
  createdAt: IsoDateTime;
}

/** What a `DocPageEvent` records. */
export type DocPageEventType = "moved" | "renamed";

/**
 * A move or a rename, recorded in a page's history (FR-DOC-10) alongside its
 * revisions, but not one itself.
 *
 * `before` and `after` are a name snapshot at the moment the event happened —
 * the old and new folder name for a move, the old and new title for a
 * rename — not a folder id or a live lookup. A folder renamed after the fact
 * must not silently rewrite what an old "Moved from Handbook to Finance"
 * entry says; the event is a record of what happened, not a formula that
 * recomputes it.
 *
 * Never restorable, unlike a `DocRevision`: there is no content here to put
 * back, only a fact about where the page was or what it was called.
 */
export interface DocPageEvent {
  id: Id;
  pageId: Id;
  occurredAt: IsoDateTime;
  actorUserId: Id;
  type: DocPageEventType;
  before: string;
  after: string;
}

/**
 * What someone may do in a folder.
 *
 * Ordered, and the order is load-bearing: a check is "at least Read", not
 * "is Read". `none` is an explicit denial rather than an absence, which is
 * what lets a descendant grant revoke access an ancestor gave.
 */
export type DocPermissionLevel =
  "none" | "read" | "comment" | "write" | "manage";

/** Rank for comparison. Higher is more. */
export const DOC_PERMISSION_RANK: Record<DocPermissionLevel, number> = {
  none: 0,
  read: 1,
  comment: 2,
  write: 3,
  manage: 4,
};

/**
 * A grant on a folder, to a user or a group.
 *
 * Grants live on folders, never on pages: a page inherits its folder's
 * effective level. Per-page grants would double the surface someone has to
 * reason about to answer "who can read this".
 */
export interface DocGrant {
  id: Id;
  folderId: Id;
  principalType: "user" | "group";
  principalId: Id;
  level: DocPermissionLevel;
  createdAt: IsoDateTime;
}

/** A file on a page, stored in R2 (FR-DOC-12). */
export interface DocAttachment {
  id: Id;
  pageId: Id;
  filename: string;
  contentType: string;
  sizeBytes: number;
  /**
   * R2 object key. Never a URL a client sees: the bucket is private, and the
   * bytes are always streamed back through the API's own permission-checked
   * route (`GET /api/docs/attachments/:id`), not a presigned link — see that
   * route's own comment, and the PR that added it, for why.
   */
  storageKey: string;
  uploadedByUserId: Id;
  uploadedAt: IsoDateTime;
}

/** A folder with the level the current user effectively holds on it. */
export interface DocFolderWithAccess extends DocFolder {
  /**
   * Resolved from the nearest ancestor carrying an explicit grant, overridden
   * by any grant on the folder itself (FR-DOC-8).
   */
  effectiveLevel: DocPermissionLevel;
}

/** A page with the revision being read and the level held on its folder. */
export interface DocPageWithContent extends DocPage {
  revision: DocRevision;
  effectiveLevel: DocPermissionLevel;
  attachments: DocAttachment[];
}
